import React, { useCallback, useContext, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button } from '@mui/material';
import format from 'string-template';

import CoachTaskAdminContext from '../../context/CoachTaskAdminContext';
import GenericDataGrid from '../../components/GenericDataGrid/GenericDataGrid';
import QuickSearchToolbar from '../../components/QuickSearchToolbar';
import useQuickSearch from '../../../hooks/useQuickSearch';
import Select, { ListSubheader, MenuItem } from '../../../components/Select';
import {
  HeaderRow,
  TitleContainer,
  Title,
  SubTitle,
} from '../../../components/v2/Header';

import { CoachSet } from './utils';
import {
  PageContainer,
  GridWrapper,
  OverdueText,
  CoachSetControl,
} from './styles';
import texts from './texts.json';

/*
  Most coaches have no tasks, so an ascending first click would leave the list looking unsorted:
  the counts sort with the most first.
*/
const MOST_FIRST = ['desc', 'asc'];

const CoachTaskAdminDashboard = () => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const {
    isReady,
    coachSet,
    setCoachSet,
    camOptions,
    activeCoachIds,
    coachNameById,
    countsByCoach,
  } = useContext(CoachTaskAdminContext);

  // Memoized: useQuickSearch filters again whenever its rows change.
  const rows = useMemo(() => activeCoachIds.map((coachId) => ({
    id: coachId,
    name: coachNameById[coachId] || coachId,
    open: countsByCoach[coachId]?.open || 0,
    completed: countsByCoach[coachId]?.completed || 0,
    overdue: countsByCoach[coachId]?.overdue || 0,
  })), [activeCoachIds, coachNameById, countsByCoach]);

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: texts.columns.coach,
      flex: 2,
      quickSearch: true,
    },
    {
      field: 'open',
      headerName: texts.columns.open,
      type: 'number',
      flex: 1,
      sortingOrder: MOST_FIRST,
    },
    {
      field: 'completed',
      headerName: texts.columns.completed,
      type: 'number',
      flex: 1,
      sortingOrder: MOST_FIRST,
    },
    {
      field: 'overdue',
      headerName: texts.columns.overdue,
      type: 'number',
      flex: 1,
      sortingOrder: MOST_FIRST,
      renderCell: ({ value }) => <OverdueText $hasOverdue={value > 0}>{value}</OverdueText>,
    },
    {
      field: 'actions',
      headerName: texts.columns.actions,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => history.push(`${url}/${row.id}`)}
        >
          {texts.viewTasks}
        </Button>
      ),
    },
  ], [history, url]);

  const { filteredRows, toolbarProps } = useQuickSearch(rows, columns);

  const coachSetOptions = useMemo(() => [
    { value: CoachSet.MINE, label: texts.coachSets.mine },
    { value: CoachSet.ALL, label: texts.coachSets.all },
    ...camOptions.map(({ id, name }) => ({ value: id, label: format(texts.coachSets.cam, { name }), isCam: true })),
  ], [camOptions]);

  // The CAMs sit under their own heading, after My coaches and All coaches.
  const parseCoachSetOptions = useCallback((options) => {
    const toMenuItem = ({ value, label }) => <MenuItem key={value} value={value}>{label}</MenuItem>;
    const camItems = options.filter(({ isCam }) => isCam);
    return [
      ...options.filter(({ isCam }) => !isCam).map(toMenuItem),
      ...(camItems.length ? [<ListSubheader key="cams">{texts.coachSets.byCam}</ListSubheader>] : []),
      ...camItems.map(toMenuItem),
    ];
  }, []);

  return (
    <PageContainer>
      <HeaderRow>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.subtitle}</SubTitle>
        </TitleContainer>
        <CoachSetControl>
          <Select
            label={texts.coachSets.label}
            value={coachSet}
            options={coachSetOptions}
            optionParserFunction={parseCoachSetOptions}
            onChange={(event) => setCoachSet(event.target.value)}
          />
        </CoachSetControl>
      </HeaderRow>
      <GridWrapper>
        <GenericDataGrid
          rows={filteredRows}
          columns={columns}
          loading={!isReady}
          slots={{ toolbar: QuickSearchToolbar }}
          slotProps={{
            toolbar: {
              ...toolbarProps,
              placeholder: texts.searchPlaceholder,
            },
          }}
        />
      </GridWrapper>
    </PageContainer>
  );
};

export default CoachTaskAdminDashboard;
